import React, { useEffect, useRef, useState } from "react";
import { Box, CircularProgress, TextField, Tooltip, Typography } from "@mui/material";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import { phappsRequest } from "@/services/apiService";
import { API_PATHS } from "@/config/apiConstants";

const fmt = (n) =>
  n == null ? "—" : `$${Number(n).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

/**
 * Click-to-edit invoiced amount for a single script on an invoice.
 */
const EditableInvAmount = ({ script, onSaved, onError }) => {
  const locked = !!script.PaymentID || Number(script.WrittenOff) === -1;
  const lockReason = script.PaymentID
    ? "Paid — reverse the payment to reprice"
    : "Written off — reverse the write-off to reprice";

  const [editing, setEditing] = useState(false);
  const [value, setValue] = useState("");
  const [saving, setSaving] = useState(false);
  const inputRef = useRef(null);

  useEffect(() => {
    if (editing) {
      setValue(script.InvAmount == null ? "" : String(Number(script.InvAmount).toFixed(2)));
      setTimeout(() => inputRef.current?.select?.(), 0);
    }
  }, [editing, script.InvAmount]);

  const cancel = () => { setEditing(false); setValue(""); };

  const commit = async () => {
    if (saving) return;
    const trimmed = String(value).trim();
    const next = Number(trimmed);

    if (trimmed === "" || !Number.isFinite(next) || next < 0) {
      onError?.("Enter a valid amount (0 or more).");
      cancel();
      return;
    }
    
    if (Math.round(next * 100) === Math.round(Number(script.InvAmount ?? 0) * 100)) {
      cancel();
      return;
    }

    setSaving(true);
    try {
      await phappsRequest(API_PATHS.ZEPHYR_ADMIN_SCRIPT_AMOUNT(script.ScriptID), "PUT", {
        invAmount: next,
      });
      setEditing(false);
      onSaved?.();
    } catch (e) {
      onError?.(e?.message || "Failed to update the price.");
      cancel();
    } finally {
      setSaving(false);
    }
  };

  if (locked) {
    return (
      <Tooltip title={lockReason} arrow>
        <Typography component="span" variant="body2" sx={{ fontSize: "0.8rem", color: "text.secondary" }}>
          {fmt(script.InvAmount)}
        </Typography>
      </Tooltip>
    );
  }

  if (editing) {
    return (
      <TextField
        inputRef={inputRef}
        autoFocus
        size="small"
        type="number"
        value={value}
        disabled={saving}
        onChange={(e) => setValue(e.target.value)}
        onBlur={commit}
        onKeyDown={(e) => {
          if (e.key === "Enter") { e.preventDefault(); commit(); }
          if (e.key === "Escape") { e.preventDefault(); cancel(); }
        }}
        inputProps={{ step: "0.01", min: 0, style: { textAlign: "right", padding: "2px 6px", fontSize: "0.8rem" } }}
        InputProps={{
          startAdornment: <Box component="span" sx={{ fontSize: "0.8rem", color: "text.secondary", mr: 0.25 }}>$</Box>,
          endAdornment: saving ? <CircularProgress size={12} /> : null,
        }}
        sx={{ width: 110, "& .MuiOutlinedInput-root": { pr: 0.5 } }}
      />
    );
  }

  return (
    <Tooltip title="Click to edit price" arrow>
      <Box
        component="button"
        type="button"
        onClick={() => setEditing(true)}
        sx={{
          display: "inline-flex", alignItems: "center", gap: 0.5,
          border: "1px solid transparent", borderRadius: "4px",
          background: "none", cursor: "pointer", px: 0.5, py: "1px",
          font: "inherit", fontSize: "0.8rem", color: "inherit",
          "&:hover": { borderColor: "#cbd5e1", background: "#f8fafc" },
          "&:hover .edit-pencil": { opacity: 1 },
        }}
      >
        {fmt(script.InvAmount)}
        <EditOutlinedIcon
          className="edit-pencil"
          sx={{ fontSize: 13, opacity: 0.25, transition: "opacity .15s", color: "#64748b" }}
        />
      </Box>
    </Tooltip>
  );
};

export default EditableInvAmount;
